import React, { useState } from 'react';
import { Building2, Users, Key, DollarSign, Bell, Shield, Database } from 'lucide-react';
import CompanySettings from './settings/CompanySettings';
import StaffManagement from './settings/StaffManagement';
import RolesPermissions from './settings/RolesPermissions';
import BillingSettings from './settings/BillingSettings';
import NotificationsSettings from './settings/NotificationsSettings';
import SecuritySettings from './settings/SecuritySettings';
import IntegrationsSettings from './settings/IntegrationsSettings';

const SettingsView = ({ currentUserRole = 'admin', currentUserId = null }) => {
  const [activeTab, setActiveTab] = useState('company');

  // Permissions
  const canManageEmployees = ['admin', 'manager'].includes(currentUserRole);
  const canDeleteEmployees = currentUserRole === 'admin';
  const canManagePasswords = currentUserRole === 'admin';

  const tabs = [
    { id: 'company', label: 'Company Info', icon: Building2, requiredRole: 'read_only' },
    { id: 'employees', label: 'Staff Management', icon: Users, requiredRole: 'manager' },
    { id: 'roles', label: 'Roles & Permissions', icon: Key, requiredRole: 'admin' },
    { id: 'billing', label: 'Billing & Fees', icon: DollarSign, requiredRole: 'manager' },
    { id: 'notifications', label: 'Notifications', icon: Bell, requiredRole: 'read_only' },
    { id: 'security', label: 'Security', icon: Shield, requiredRole: 'admin' },
    { id: 'integrations', label: 'Integrations', icon: Database, requiredRole: 'manager' }
  ];

  const accessibleTabs = tabs;

  const renderContent = () => {
    switch(activeTab) {
      case 'company':
        return <CompanySettings />;
      case 'employees':
        return <StaffManagement 
          currentUserRole={currentUserRole}
          currentUserId={currentUserId}
          canManageEmployees={canManageEmployees}
          canDeleteEmployees={canDeleteEmployees}
          canManagePasswords={canManagePasswords}
        />;
      case 'roles':
        return <RolesPermissions currentUserRole={currentUserRole} />;
      case 'billing':
        return <BillingSettings />;
      case 'notifications':
        return <NotificationsSettings />;
      case 'security':
        return <SecuritySettings />;
      case 'integrations':
        return <IntegrationsSettings />;
      default:
        return <CompanySettings />;
    }
  };

  return (
    <div className="space-y-4 md:space-y-6 px-2 sm:px-0">
      {/* Header - Responsive */}
      <div className="bg-gray-800/50 rounded-xl p-4 sm:p-6 border border-gray-700">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-4">
          <div className="p-2 bg-blue-500/20 rounded-lg">
            <Building2 className="w-5 h-5 sm:w-6 sm:h-6 text-blue-400" />
          </div>
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-white">Settings</h2>
            <p className="text-gray-400 text-xs sm:text-sm">Manage your company, employees, and system preferences</p>
          </div>
        </div>

        {/* Tabs - Scrollable horizontalement sur mobile */}
        <div className="flex gap-2 border-b border-gray-700 pb-2 overflow-x-auto whitespace-nowrap">
          {accessibleTabs.map(tab => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg transition-all text-xs sm:text-sm ${
                  activeTab === tab.id
                    ? 'bg-blue-500/20 text-blue-400 border border-blue-500/30'
                    : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <Icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Content */}
      <div className="overflow-x-auto">
        {renderContent()}
      </div>
    </div>
  );
};

export default SettingsView;